// Setor3Page.jsx
// Manutenção — Setor 3 (VW_BSP_SERVICOS_ONLINE)
// Requisitos: @mui/material, @mui/icons-material, dayjs
import * as React from 'react';
import {
  Container, Paper, Grid, Typography, TextField, Button, Box,
  CircularProgress, Alert,
  InputAdornment, IconButton, Card, CardContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import Lottie from 'lottie-react';
import mechanicAnimation from '../assets/mechanic animation.json';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import { useThemeMode } from '../context/ThemeContext';
import { DataGrid } from '@mui/x-data-grid';
import alertaOS from '../assets/AlertaOS.mp3'; // ajuste o caminho se o Setor3Page estiver em outra pasta
import MapaVeiculos from './Mapa_Veiculos';
import MediaAbastecimento from './Media_Abastecimento';

// Lottie que não re-renderiza com mudanças de estado (evita removeChild em updates)
const StaticLottie = React.memo(
  function StaticLottie() {
    // guarda a instância criada internamente pelo lottie-react
    const lottieRef = React.useRef(null);

    // IMPORTANTE: em modo DEV (StrictMode) o React monta/desmonta duas vezes.
    // Aqui garantimos que, se a lib já destruiu internamente, não tentaremos destruir de novo.
    React.useEffect(() => {
      return () => {
        const inst = lottieRef.current;
        // se existir e ainda não foi destruída, destruímos uma única vez
        if (inst && typeof inst.destroy === 'function') {
          try { inst.destroy(); } catch {}
        }
        // zera a ref para evitar destroy duplo em novos renders DEV
        lottieRef.current = null;
      };
    }, []);

    return (
      <Box sx={{ width: { xs: 56, sm: 72, md: 80 } }}>
        <Lottie
          // passamos a ref para controlar a instância e evitar "removeChild" duplicado
          lottieRef={lottieRef}
          animationData={mechanicAnimation}
          loop
          autoplay
        />
      </Box>
    );
  },
  // nunca re-renderiza depois do primeiro paint
  () => true
);


// Tempo do auto-refresh em segundos (ex.: 60 = 1 min; 300 = 5 min)
const REFRESH_SECONDS = 300;

// Formata segundos em "MM:SS" (ex.: 300 -> "05:00")
function formatSecondsToMMSS(total) {
  const s = Math.max(0, total | 0);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

// ===== Config (suporta Vite e CRA) =====
const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const API_KEY =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_KEY) ||
  process.env.REACT_APP_API_KEY ||
  '';

// ===== Utils =====
const toNum = (v) => {
  if (v == null) return 0;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const norm = (v) => (v ?? '').toString().trim();
const normCode = (v) => norm(v).toUpperCase();

// Pequeno "atraso" para não filtrar a cada tecla
function useDebouncedValue(value, delay = 200) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const fmt = {
  // "YYYY-MM-DD" -> "DD/MM/YYYY"
  ymdToBR(ymd) {
    if (!ymd) return '';
    const d = dayjs(ymd, 'YYYY-MM-DD');
    return d.isValid() ? d.format('DD/MM/YYYY') : '';
  },
  // Date -> "YYYY-MM-DD"
  dateToYmd(d) {
    return dayjs(d).format('YYYY-MM-DD');
  },
  // ISO -> "DD/MM/YYYY"
  isoToBR(iso) {
    const d = dayjs(iso);
    return d.isValid() ? d.format('DD/MM/YYYY') : '';
  },
};

// ===== MKBF (igual ao script antigo): média mensal de km/atendimentos (SOCORRO/ATENDIMENTO EXTERNO/R.A)
function calcularMKBF(dados, startYmd) {
  if (!Array.isArray(dados) || !dados.length) return 0;

  const referenciaMes = dayjs(startYmd, 'YYYY-MM-DD', true);
  const mesesMap = new Map();

  dados.forEach((row) => {
    const data = dayjs(row.DATA); // ISO no backend
    if (!data.isValid()) return;

    // usa o mês/ano do startDate como referência
    if (data.month() !== referenciaMes.month() || data.year() !== referenciaMes.year()) {
      return;
    }

    const km = parseFloat(row.KM);
    const qtde = parseInt(row.QTDE);
    const origem = (row.ORIGEM || '').toUpperCase();

    if (isNaN(km) || isNaN(qtde) || !origem) return;

    const mesChave = data.format('YYYY-MM');

    if (!mesesMap.has(mesChave)) {
      mesesMap.set(mesChave, {
        km,
        atendimentos: ['SOCORRO', 'ATENDIMENTO EXTERNO', 'R.A'].includes(origem) ? qtde : 0,
      });
    } else {
      const item = mesesMap.get(mesChave);
      if (['SOCORRO', 'ATENDIMENTO EXTERNO', 'R.A'].includes(origem)) {
        item.atendimentos += qtde;
      }
    }
  });

  const mkbfs = [...mesesMap.values()]
    .map(({ km, atendimentos }) => (atendimentos > 0 ? km / atendimentos : 0))
    .filter((mkbf) => mkbf > 0);

  const mediaMkbf = mkbfs.length ? mkbfs.reduce((a, b) => a + b, 0) / mkbfs.length : 0;
  return Number(mediaMkbf.toFixed(2));
}

// ===== Cores por card (editáveis e independentes) =====
// Edite livremente cada card abaixo: bg = fundo, value = cor do número, label = cor do rótulo
const CARD_COLORS = {
  ORDENS:   { bg: '#01CDFE', value: '#FFFFFF', label: '#FFFFFF' },
  ABERTA:   { bg: '#FF1744', value: '#FFFFFF', label: '#FFFFFF' },      // exemplo: vermelho sólido
  FECHADA:  { bg: '#00B159', value: '#FFFFFF', label: '#FFFFFF' },
  PENDENTE: { bg: '#FFC425', value: '#FFFFFF', label: '#FFFFFF' },
  CORRETIVA:{ bg: '#92A8D1', value: '#FFFFFF', label: '#FFFFFF' },
  PREVENTIVA:{bg: '#FFAD60', value: '#FFFFFF', label: '#FFFFFF' },
  RA:       { bg: '#C9C9FF', value: '#FFFFFF', label: '#FFFFFF' },      // R.A agora é INDEPENDENTE
  SOCORRO:  { bg: '#0086AD', value: '#FFFFFF', label: '#FFFFFF' },
  EXTERNO:  { bg: '#83ADB5', value: '#FFFFFF', label: '#FFFFFF' },
  MKBF:     { bg: '#B967FF', value: '#FFFFFF', label: '#FFFFFF' },
  KM_MEDIA: { bg: '#2E7D32', value: '#FFFFFF', label: '#FFFFFF' },
};

// Converte a chave do card no formato esperado pelo componente (bgcolor/fgcolor/labelcolor)
const mapColors = (key) => {
  const c = CARD_COLORS[key] || {};
  return { bgcolor: c.bg, fgcolor: c.value, labelcolor: c.label };
};


// ===== API (usa a view VW_BSP_SERVICOS_ONLINE) =====
// GET /api/setor3/servicos?startDate=DD/MM/YYYY&endDate=DD/MM/YYYY[&limit=N]
async function fetchServicos({ startDate, endDate, signal }) {
  const sp = new URLSearchParams();
  if (startDate) sp.set('startDate', startDate);
  if (endDate) sp.set('endDate', endDate);
  // Sem "limit" — pedido do cliente (campo virou busca por veículo)

  const url = `${API_BASE}/api/setor3/servicos?${sp.toString()}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: API_KEY,   // middleware aceita Authorization e/ou x-api-key
      'x-api-key': API_KEY,
    },
    signal,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Falha ao carregar serviços (${res.status}) — ${text}`);
  }
  const rows = await res.json();
  return Array.isArray(rows) ? rows : [];
}

// ===== KPIs (sempre somando QTDE_OS) =====
function useKpis(rowsFiltered) {
  return React.useMemo(() => {
    const sumQTDE = (acc, r) => acc + toNum(r.QTDE_OS);
    const totalQtdeOS = rowsFiltered.reduce(sumQTDE, 0);

    const agg = (pred) =>
      rowsFiltered.filter(pred).reduce(sumQTDE, 0);

    // CONDICAOOS
    const osAB = agg((r) => normCode(r.CONDICAOOS) === 'AB');
    const osFC = agg((r) => normCode(r.CONDICAOOS) === 'FC');
    const osPE = agg((r) => normCode(r.CONDICAOOS) === 'PE');

    // ORIGEM
    const origemCorretiva  = agg((r) => normCode(r.ORIGEM) === 'CORRETIVA');
    const origemPreventiva = agg((r) => normCode(r.ORIGEM) === 'PREVENTIVA');
    const origemRA         = agg((r) => normCode(r.ORIGEM) === 'R.A');
    const origemSocorro    = agg((r) => normCode(r.ORIGEM) === 'SOCORRO');
    const origemAtExterno  = agg((r) => normCode(r.ORIGEM) === 'ATENDIMENTO EXTERNO');

    return {
      totalQtdeOS, osAB, osFC, osPE,
      origemCorretiva, origemPreventiva, origemRA, origemSocorro, origemAtExterno,
    };
  }, [rowsFiltered]);
}

// ======== animações e estilos dos cards (igual ao script antigo) ========
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

// Card de indicador com o mesmo layout do script antigo
function IndicatorCard({ label, value, bgcolor, fgcolor, labelcolor, animation, onClick }) {

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const bg = bgcolor || (isDark ? theme.palette.grey[900] : theme.palette.background.paper);
  const fg = fgcolor || (isDark ? '#FFFFFF' : theme.palette.text.primary);
  const sub = labelcolor || (isDark ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary);

  return (
    <Card
      onClick={onClick}
      elevation={0} // evita overlay do MUI em dark mode
      sx={{
        width: 200,
        minHeight: 86,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        borderRadius: 3,
        // mantém a sombra manual (não depende de elevation)
        boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
        cursor: 'default',
        transition: 'transform 0.2s',
        animation,
        bgcolor: bg,
        color: fg,
        // desliga o gradiente/overlay que “desbota” a cor
        backgroundImage: 'none',
        opacity: 1,
        '&:hover': { transform: 'translateY(-2px)' },
      }}
    >

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: sub }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}


// ===== Página =====
export default function Setor3Page() {
  // 1) Datas já no dia atual (pré-filtrado)
  const todayYmd = fmt.dateToYmd(new Date());

  const [filters, setFilters] = React.useState({
    startYmd: todayYmd,
    endYmd: todayYmd,
    veiculo: '', // busca por PREFIXO
  });

  
  // buffer do input de veículo (o TextField edita aqui; o filtro só usa ao aplicar)
  const [veiculoQuery, setVeiculoQuery] = React.useState('');
  const searchInputRef = React.useRef(null);
  const veiculoQueryDebounced = useDebouncedValue(veiculoQuery, 200);

  const [rows, setRows] = React.useState([]);
  const [rows30d, setRows30d] = React.useState([]);
  const [condicaoFilter, setCondicaoFilter] = React.useState(''); // 'AB' | 'FC' | 'PE' | ''
  const [origemFilter, setOrigemFilter] = React.useState('');     // 'CORRETIVA' | 'PREVENTIVA' | 'R.A' | 'SOCORRO' | 'ATENDIMENTO EXTERNO' | ''
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [mkbfValue, setMkbfValue] = React.useState(0);
  const [kmMediaCard, setKmMediaCard] = React.useState(0);  
  const [countdown, setCountdown] = React.useState(REFRESH_SECONDS);
  const [isInteracting, setIsInteracting] = React.useState(false);
  const { mode, setMode } = useThemeMode();
  const audioOsRef = React.useRef(null);
  const prevTotalOsRef = React.useRef(null);
  const mediaTableRef = React.useRef(null);

  const load = async (signal) => {
    setLoading(true);
    setError('');
    try {
      const startDate = fmt.ymdToBR(filters.startYmd);
      const endDate = fmt.ymdToBR(filters.endYmd);

      // 1) serviços
      const data = await fetchServicos({ startDate, endDate, signal });
      setRows(data);

      // ===== Também carrega um dataset fixo dos últimos 15 dias (independente dos pickers) =====
      try {
        // Dataset fixo: últimos 15 dias até hoje (para tabela de OS em aberto)
        const end15Ymd = todayYmd; // já está em YYYY-MM-DD
        const start15Ymd = dayjs(end15Ymd, 'YYYY-MM-DD')
          .subtract(14, 'day') // 15 dias contando o dia final
          .format('YYYY-MM-DD');

        const start15BR = fmt.ymdToBR(start15Ymd); // vira DD/MM/YYYY
        const end15BR = fmt.ymdToBR(end15Ymd);

        const data15 = await fetchServicos({ startDate: start15BR, endDate: end15BR, signal });
        setRows30d(Array.isArray(data15) ? data15 : []);
      } catch (e) {
        // Se falhar, não quebra a página — apenas deixa a tabela de dias anteriores vazia
        if (!(e && (e.name === 'AbortError' || String(e).toLowerCase().includes('abort')))) {
          console.warn('Falha ao carregar últimos 15 dias:', e);
          setRows30d([]);
        }
      }

      // 2) mkbf (igual ao script antigo: GET /api/setor3/mkbf)
      const mkbfRes = await fetch(`${API_BASE}/api/setor3/mkbf`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: API_KEY,
          'x-api-key': API_KEY,
        },
        signal,
      });
      const mkbfJson = mkbfRes.ok ? await mkbfRes.json() : [];
      const mkbf = calcularMKBF(mkbfJson, filters.startYmd);
      setMkbfValue(Number.isFinite(mkbf) ? mkbf : 0);

      // 3) Km Média Por Litro (VW_BSP_CRITICACONSUMO)
      //    -> sempre considera o MÊS do startYmd (independente do dia selecionado)
      try {
        const baseYmd = filters.startYmd || todayYmd;

        // primeiro e último dia do mês do startYmd
        const startMonthYmd = dayjs(baseYmd, 'YYYY-MM-DD')
          .startOf('month')
          .format('YYYY-MM-DD');

        const endMonthYmd = dayjs(baseYmd, 'YYYY-MM-DD')
          .endOf('month')
          .format('YYYY-MM-DD');

        const startMonthBR = fmt.ymdToBR(startMonthYmd);
        const endMonthBR = fmt.ymdToBR(endMonthYmd);

        const spMedia = new URLSearchParams();
        spMedia.set('startDate', startMonthBR);
        spMedia.set('endDate', endMonthBR);

        const mediaRes = await fetch(
          `${API_BASE}/api/setor3/media-abastecimento?${spMedia.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: API_KEY,
              'x-api-key': API_KEY,
            },
            signal,
          }
        );

        if (mediaRes.ok) {
          const mediaJson = await mediaRes.json();
          const rawValue = Number(mediaJson?.cardKmMediaPorLitro ?? 0);
          setKmMediaCard(Number.isFinite(rawValue) ? rawValue : 0);
        } else {
          setKmMediaCard(0);
        }
      } catch (e) {
        if (
          e &&
          (e.name === 'AbortError' ||
            String(e).toLowerCase().includes('abort'))
        ) {
          // ignora abort do fetch
        } else {
          console.warn('Falha ao carregar média de abastecimento:', e);
          setKmMediaCard(0);
        }
      }

    } catch (e) {
      // Ignora abortos do fetch (React StrictMode aborta a 1ª montagem em DEV)
      if (e && (e.name === 'AbortError' || String(e).toLowerCase().includes('abort'))) {
        // não marca erro e não limpa dados
      } else {
        setError(e.message || String(e));
        setRows([]);
        setMkbfValue(0);
        setKmMediaCard(0);
      }
    } finally {
      // sempre encerra o loading, com sucesso ou erro/abort
      setLoading(false);
    }
  };

  // Carrega na montagem com período = hoje
  React.useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh a cada 60s com contagem regressiva
  React.useEffect(() => {
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          const ac = new AbortController();
          load(ac.signal);
          return REFRESH_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startYmd, filters.endYmd, filters.veiculo, isInteracting]);

  // 2) Filtro por veículo (PREFIXO) — contém, case-insensitive
  // Em tempo real: usa o valor com "atrasinho" (debounce)
  const rowsFiltered = React.useMemo(() => {
    let list = rows;

    // 2.1 filtro por veículo (prefixo) — contém, case-insensitive
    const q = norm(veiculoQueryDebounced).toLowerCase();
    if (q) {
      list = list.filter((r) =>
        norm(String(r.PREFIXO || '')).toLowerCase().includes(q)
      );
    }

    // 2.2 filtro por CONDIÇÃO (card: AB/FC/PE)
    if (condicaoFilter) {
      list = list.filter((r) => normCode(r.CONDICAOOS) === condicaoFilter);
    }

    // 2.3 filtro por ORIGEM (card: CORRETIVA/PREVENTIVA/R.A/SOCORRO/ATENDIMENTO EXTERNO)
    if (origemFilter) {
      list = list.filter(
        (r) => normCode(r.ORIGEM) === normCode(origemFilter)
      );
    }

    return list;
  }, [rows, veiculoQueryDebounced, condicaoFilter, origemFilter]);



  // 3) Cards sempre somam QTDE_OS no conjunto filtrado
  const kpis = useKpis(rowsFiltered);
  React.useEffect(() => {
    const atual = kpis.totalQtdeOS || 0;
    const anterior = prevTotalOsRef.current;

    // Só toca se já tinha um valor antes E o novo valor aumentou
    if (anterior != null && atual > anterior) {
      const audio = audioOsRef.current;
      if (audio) {
        try {
          audio.currentTime = 0;
          audio.play().catch(() => {
            // Se o navegador bloquear o som automático, só ignora
          });
        } catch (e) {
          // Ignora qualquer erro do player
        }
      }
    }

    // Atualiza o valor guardado para a próxima comparação
    prevTotalOsRef.current = atual;
  }, [kpis.totalQtdeOS]);

  const onApply = () => {
    // aplica o valor do buffer no filtro antes de recarregar
    setFilters((f) => ({ ...f, veiculo: veiculoQuery }));
    const ac = new AbortController();
    load(ac.signal);
    setCountdown(REFRESH_SECONDS); // reinicia a contagem sempre que atualizar manualmente
  };


  // restaurar a página para o estado inicial (datas = hoje, veículo vazio) e recarregar
  const onRestore = () => {
    const today = fmt.dateToYmd(new Date());
    setVeiculoQuery(''); // zera buffer do input
    setFilters({ startYmd: today, endYmd: today, veiculo: '' });
    const ac = new AbortController();
    load(ac.signal);
    setCountdown(REFRESH_SECONDS);
  };

  // === DataGrid: colunas e linhas ===
  const dgColumns = React.useMemo(
    () => [
      { field: 'DATA', headerName: 'DATE', flex: 0.9, minWidth: 110,
        headerAlign: 'center', align: 'center',
        renderCell: (params) => {
          const v = params?.row?.DATA ?? '';
          if (!v) return '';
          // Se já vier "DD/MM/YYYY", mantém
          if (typeof v === 'string' && v.includes('/')) return v;
          // Se vier ISO "YYYY-MM-DD", converte
          return fmt.isoToBR(v);
        }
      },

      { field: 'HORA', headerName: 'HORA', flex: 0.7, minWidth: 90,
        headerAlign: 'center', align: 'center' },
      { field: 'NUMERO_OS', headerName: 'O. SERVIÇO', flex: 0.8, minWidth: 110,
        headerAlign: 'center', align: 'center' },
      { field: 'PREFIXO', headerName: 'VEÍCULO', flex: 0.8, minWidth: 110,
        headerAlign: 'center', align: 'center' },
      { field: 'ORIGEM', headerName: 'ORIGEM', flex: 1, minWidth: 120,
        headerAlign: 'center', align: 'center',
        renderCell: (params) => {
          const row = params?.row || {};
          const origem = row.ORIGEM || '';
          const tipo = row.TIPO_OS || '';
          return `${origem}${tipo ? ' • ' + tipo : ''}`;
        }
      },
      { field: 'GRUPO_SERVICO', headerName: 'GRUPO', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' },
      { field: 'SERVICO', headerName: 'SERVIÇO', flex: 1, minWidth: 140, headerAlign: 'center', align: 'center' },
      { field: 'CONDICAOOS', headerName: 'CONDIÇÃO', flex: 0.8, minWidth: 110, headerAlign: 'center', align: 'center' },
      { field: 'DT_FECHAMENTO', headerName: 'FECHAMENTO', flex: 1, minWidth: 130, headerAlign: 'center', align: 'center' },
      { field: 'MECANICO', headerName: 'MECÂNICO', flex: 1, minWidth: 150, headerAlign: 'center', align: 'center' },
    ],
    []
  );

  const dgColumnsAberto = React.useMemo(
    () => [
      {
        field: 'DATA',
        headerName: 'DATA',
        flex: 0.9,
        minWidth: 110,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          const v = params?.row?.DATA ?? '';
          if (!v) return '';
          // Se já vier "DD/MM/YYYY", mantém
          if (typeof v === 'string' && v.includes('/')) return v;
          // Se vier ISO "YYYY-MM-DD", converte
          return fmt.isoToBR(v);
        },
      },
      {
        field: 'HORA',
        headerName: 'HORA',
        flex: 0.7,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'USUARIOABERTURAOS',
        headerName: 'RESP. ABERTURA',
        flex: 1.2,
        minWidth: 150,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'NUMERO_OS',
        headerName: 'O. SERVIÇO',
        flex: 0.8,
        minWidth: 110,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'PREFIXO',
        headerName: 'VEÍCULO',
        flex: 0.8,
        minWidth: 110,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'ORIGEM',
        headerName: 'ORIGEM',
        flex: 1,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
      },
    ],
    []
  );

  // Gera ID único para cada linha (evita reaproveitamento errado)
  const dgRows = React.useMemo(
    () =>
      (rowsFiltered || []).map((r, i) => ({
        id: `${r.NUMERO_OS || 'os'}-${r.PREFIXO || 'px'}-${r.DATA || ''}-${r.HORA || ''}-${i}`,
        ...r,
      })),
    [rowsFiltered]
  );

  const dgRowsAberto = React.useMemo(
    () => {
      const hoje = dayjs(todayYmd, 'YYYY-MM-DD');

      // 1) Filtra apenas OS em ABERTO de dias anteriores
      const filtradas = (rows30d || []).filter((r) => {
        if (normCode(r.CONDICAOOS) !== 'AB') return false;

        const rawData = r.DATA || r.DT_FECHAMENTO;
        if (!rawData) return false;

        let d;
        if (typeof rawData === 'string' && rawData.includes('/')) {
          d = dayjs(rawData, 'DD/MM/YYYY');
        } else {
          d = dayjs(rawData);
        }

        if (!d.isValid()) return false;

        return d.isBefore(hoje, 'day');
      });

      // 2) Deixa apenas UMA linha por OS (e veículo)
      const vistos = new Set();
      const unicos = [];

      filtradas.forEach((r) => {
        const chave = `${r.NUMERO_OS || ''}-${r.PREFIXO || ''}`;

        if (!vistos.has(chave)) {
          vistos.add(chave);
          unicos.push(r);
        }
      });

      // 3) Monta as linhas da tabela
      return unicos.map((r, i) => ({
        id: `aberto-${r.NUMERO_OS || 'os'}-${r.PREFIXO || 'px'}-${r.DATA || ''}-${r.HORA || ''}-${i}`,
        ...r,
      }));
    },
    [rows30d, todayYmd]
  );

  const handleOpenMediaAbastecimento = () => {
    if (mediaTableRef.current) {
      mediaTableRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <Container component="main" maxWidth={false} disableGutters sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, md: 2 } }}>
      <audio
        ref={audioOsRef}
        preload="auto"
        src={alertaOS}
      />

      {/* Filtros */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2, width: '100%' }}>
        {/* Linha única com esquerda (Lottie + filtros) e direita (status/ações) */}
        <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
          {/* ESQUERDA: Lottie + 2 datas + ATUALIZAR */}
          <Grid item xs={12} md>
            <Grid container alignItems="center" spacing={2} wrap="wrap">
              <Grid item>
                <StaticLottie />
              </Grid>
              <Grid item>
                <TextField
                  label="Dados"
                  type="date"
                  value={filters.startYmd}
                  onChange={(e) => setFilters((f) => ({ ...f, startYmd: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 190 }}
                />
              </Grid>

              <Grid item>
                <TextField
                  label="Filme de dados"
                  type="date"
                  value={filters.endYmd}
                  onChange={(e) => setFilters((f) => ({ ...f, endYmd: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 190 }}
                />
              </Grid>

              {/* Se quiser manter a busca por veículo, deixe este bloco; se não, remova */}
              <Grid item>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onRestore}
                  sx={{ minWidth: 120 }}
                >
                  RESTAURAR
                </Button>
              </Grid>              
              <Grid item>
                <Button
                  variant="contained"
                  onClick={onApply}
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  {loading ? 'CARREGANDO…' : 'ATUALIZAR'}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {/* DIREITA: status de atualização + ícones + voltar */}
          <Grid item xs={12} md="auto">
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              gap: 1.5,
              mt: { xs: 1, md: 0 }
            }}>
              {/* Contagem + loader (sem texto) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} thickness={5} />
                <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'right' }}>
                  {formatSecondsToMMSS(countdown)}
                </Typography>
              </Box>

              <IconButton
                size="small"
                color="inherit"
                title="Alternar tema"
                onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
              >
                {mode === 'dark' ? (
                  <LightModeRoundedIcon fontSize="small" />
                ) : (
                  <DarkModeRoundedIcon fontSize="small" />
                )}
              </IconButton>

              <Button variant="outlined" onClick={() => window.history.back()}>
                VOLTAR
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* CARDS (somando QTDE_OS sobre o conjunto filtrado) */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item>
          <IndicatorCard
            label="ORDENS DE SERVIÇO"
            value={kpis.totalQtdeOS}
            onClick={() => { setCondicaoFilter(''); setOrigemFilter(''); }}
            {...mapColors('ORDENS')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="OS ABERTA"
            value={kpis.osAB}
            animation={`${pulse} 1s infinite`}
            onClick={() => {
              setOrigemFilter('');
              setCondicaoFilter((prev) => (prev === 'AB' ? '' : 'AB'));
            }}
            {...mapColors('ABERTA')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="OS FECHADA"
            value={kpis.osFC}
            onClick={() => {
              setOrigemFilter('');
              setCondicaoFilter((prev) => (prev === 'FC' ? '' : 'FC'));
            }}
            {...mapColors('FECHADA')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="OS PENDENTE"
            value={kpis.osPE}
            onClick={() => {
              setOrigemFilter('');
              setCondicaoFilter((prev) => (prev === 'PE' ? '' : 'PE'));
            }}
            {...mapColors('PENDENTE')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="CORRETIVA"
            value={kpis.origemCorretiva}
            onClick={() => {
              setCondicaoFilter('');
              setOrigemFilter((prev) => (prev === 'CORRETIVA' ? '' : 'CORRETIVA'));
            }}
            {...mapColors('CORRETIVA')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="PREVENTIVA"
            value={kpis.origemPreventiva}
            onClick={() => {
              setCondicaoFilter('');
              setOrigemFilter((prev) => (prev === 'PREVENTIVA' ? '' : 'PREVENTIVA'));
            }}
            {...mapColors('PREVENTIVA')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="R.A"
            value={kpis.origemRA}
            onClick={() => {
              setCondicaoFilter('');
              setOrigemFilter((prev) => (prev === 'R.A' ? '' : 'R.A'));
            }}
            {...mapColors('RA')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="SOCORRO"
            value={kpis.origemSocorro}
            onClick={() => {
              setCondicaoFilter('');
              setOrigemFilter((prev) => (prev === 'SOCORRO' ? '' : 'SOCORRO'));
            }}
            {...mapColors('SOCORRO')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="ATEND. EXTERNO"
            value={kpis.origemAtExterno}
            onClick={() => {
              setCondicaoFilter('');
              setOrigemFilter((prev) => (prev === 'ATENDIMENTO EXTERNO' ? '' : 'ATENDIMENTO EXTERNO'));
            }}
            {...mapColors('EXTERNO')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="KM MÉDIA POR LITRO"
            value={
              kmMediaCard > 0
                ? kmMediaCard.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : 0
            }
            onClick={handleOpenMediaAbastecimento}
            {...mapColors('KM_MEDIA')}
          />
        </Grid>

        <Grid item>
          <IndicatorCard
            label="MKBF"
            value={
              mkbfValue > 0
                ? mkbfValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : 0
            }
            {...mapColors('MKBF')}
          />
        </Grid>
      </Grid>

      {/* Status de carregamento/erro */}
      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabela (sempre montada; preenche quando os dados chegarem) */}
            <>        
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, width: '100%' }}>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Pesquisar Veículo"
              placeholder="Ex.: 00000"
              inputRef={searchInputRef}
              value={veiculoQuery}
              onFocus={() => setIsInteracting(true)}
              onBlur={() => setIsInteracting(false)}
              onChange={(e) => {
                const v = e.target.value;
                setVeiculoQuery(v);
                if (v === '') {
                  setFilters((f) => ({ ...f, veiculo: '' }));
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: veiculoQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Limpar"
                      size="small"
                      edge="end"
                      tabIndex={-1}
                      onClick={() => {
                        setVeiculoQuery('');
                        setFilters((f) => ({ ...f, veiculo: '' }));
                        // mantém o cursor no campo
                        if (searchInputRef.current?.focus) searchInputRef.current.focus();
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}

              sx={{ maxWidth: 260 }}
            />
          </Box>
          <Box
            sx={{
              height: 560,
              width: '100%',
              '& .MuiDataGrid-root': {
                backgroundColor: mode === 'dark' ? '#0b0e10' : '#ffffff',
                color: mode === 'dark' ? '#e8e8e8' : '#111',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: mode === 'dark' ? '#0b0e10' : '#E1E1E2',
                borderBottom: `1px solid ${mode === 'dark' ? '#E1F7D5' : '#E0E0E0'}`,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: mode === 'dark' ? '#e8e8e8' : '#4D4D4D',
                fontWeight: 600,
                textTransform: 'uppercase',
                textAlign: 'center',
                width: '100%',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'center',
              },
              '& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root, & .MuiDataGrid-sortIcon': {
                color: mode === 'dark' ? '#cfd8dc' : '#6b7280',
              },
              '& .MuiDataGrid-row': {
                backgroundColor: mode === 'dark' ? '#0b0e10' : '#ffffff',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${mode === 'dark' ? '#1f2a30' : '#E0E0E0'}`,
                alignItems: 'center',
                justifyContent: 'center',
              },
              '& .MuiDataGrid-cellContent': {
                textAlign: 'center',
                width: '100%',
              },
            }}
          >
            <DataGrid
              rows={dgRows}
              columns={dgColumns}
              rowHeight={44}
              disableRowSelectionOnClick
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 10 } },
                sorting: { sortModel: [{ field: 'HORA', sort: 'desc' }] }, // mais recente -> mais antiga
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              localeText={{ noRowsLabel: 'Sem linhas' }}
            />
          </Box>

        </Paper>

        <Paper sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, borderRadius: 2, width: '100%' }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}
          >
            ORDENS DE SERVIÇOS EM ABERTO DE DIAS ANTERIORES
          </Typography>

          <Box
            sx={{
              height: 400,
              width: '100%',
              '& .MuiDataGrid-root': {
                backgroundColor: mode === 'dark' ? '#0b0e10' : '#ffffff',
                color: mode === 'dark' ? '#e8e8e8' : '#111',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: mode === 'dark' ? '#0b0e10' : '#E1E1E2',
                borderBottom: `1px solid ${mode === 'dark' ? '#E1F7D5' : '#E0E0E0'}`,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: mode === 'dark' ? '#e8e8e8' : '#4D4D4D',
                fontWeight: 600,
                textTransform: 'uppercase',
                textAlign: 'center',
                width: '100%',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'center',
              },
              '& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root, & .MuiDataGrid-sortIcon': {
                color: mode === 'dark' ? '#cfd8dc' : '#6b7280',
              },
              '& .MuiDataGrid-row': {
                backgroundColor: mode === 'dark' ? '#0b0e10' : '#ffffff',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${mode === 'dark' ? '#1f2a30' : '#E0E0E0'}`,
                alignItems: 'center',
                justifyContent: 'center',
              },
              '& .MuiDataGrid-cellContent': {
                textAlign: 'center',
                width: '100%',
              },
            }}
          >
        <DataGrid
          rows={dgRowsAberto}
          columns={dgColumnsAberto}
          rowHeight={44}
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 10 } },
            sorting: { sortModel: [{ field: 'DATA', sort: 'desc' }] },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          localeText={{ noRowsLabel: 'Sem linhas' }}
        />
      </Box>          
        </Paper>

      <Box ref={mediaTableRef}>
        <MediaAbastecimento baseYmd={filters.startYmd || todayYmd} />
      </Box>

          <Grid item xs={12} mt={2}>
            <MapaVeiculos />
          </Grid>        
      </>
    </Container>
  );
}